import React, { useState, useEffect } from 'react';
import { toast } from '../../components/toast/CustomToast';
import Button from '../../components/common/Button';
import HighlightedPreview from '../../components/common/HighlightedPreview';
import api from '../../utils/api';
import { processTemplate, extractVariables } from '../../utils/configVariables';

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ── Variables as Badges with Values Highlighted ────────────────────────────────────────

const VariablesWithBadges = ({ text, configs, isPreview = false }) => {
    const parts = [];
    const regex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
    let lastIndex = 0;
    let match;

    // Build config map
    const configMap = {};
    configs.forEach(cfg => {
        configMap[cfg.key] = cfg.value;
    });

    while ((match = regex.exec(text)) !== null) {
        // Add text before variable
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex, match.index),
                key: `text-${lastIndex}`,
            });
        }

        const varName = match[1];
        const exists = configMap[varName] !== undefined;
        const varValue = configMap[varName];

        // Add badge variable
        parts.push({
            type: 'variable',
            content: `{{${varName}}}`,
            varName,
            exists,
            varValue,
            key: `var-${lastIndex}`,
        });

        lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push({
            type: 'text',
            content: text.substring(lastIndex),
            key: `text-final`,
        });
    }

    if (parts.length === 0) {
        return <span>{text}</span>;
    }

    // For preview, highlight the expanded variable values instead of the {{}} syntax
    if (isPreview) {
        const variables = extractVariables(text);
        if (variables.length === 0) return <span>{text}</span>;

        const previewParts = [];
        let remaining = text;

        variables.forEach((varName) => {
            const varValue = configMap[varName];
            if (varValue) {
                const idx = remaining.indexOf(varValue);
                if (idx !== -1) {
                    // Add text before the variable value
                    if (idx > 0) {
                        previewParts.push({
                            type: 'text',
                            content: remaining.substring(0, idx),
                            key: `text-${previewParts.length}`,
                        });
                    }
                    // Add highlighted variable value
                    previewParts.push({
                        type: 'variable',
                        content: varValue,
                        key: `var-${varName}-${previewParts.length}`,
                    });
                    remaining = remaining.substring(idx + varValue.length);
                }
            }
        });

        // Add remaining text
        if (remaining) {
            previewParts.push({
                type: 'text',
                content: remaining,
                key: `text-final`,
            });
        }

        return (
            <span>
                {previewParts.map((part) => (
                    part.type === 'variable' ? (
                        <span
                            key={part.key}
                            className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-200 font-semibold px-1 rounded"
                        >
                            {part.content}
                        </span>
                    ) : (
                        <span key={part.key}>{part.content}</span>
                    )
                ))}
            </span>
        );
    }

    // Regular view with badges - showing variable and its expanded value
    return (
        <span className="inline-flex items-center flex-wrap gap-1">
            {parts.map((part) => (
                part.type === 'variable' ? (
                    <span
                        key={part.key}
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-mono font-medium border ${
                            part.exists
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                                : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
                        }`}
                    >
                        <span>{part.content}</span>
                        {part.exists && part.varValue && (
                            <span className="text-blue-600 dark:text-blue-300 font-bold">→ {part.varValue}</span>
                        )}
                    </span>
                ) : (
                    <span key={part.key} className="whitespace-pre-wrap">{part.content}</span>
                )
            ))}
        </span>
    );
};

// ── Config Value Input Component ────────────────────────────────────────

const ConfigValueInput = ({ config, draft, errors, onValueChange }) => {
    const rowErrors = errors[config.id] || {};
    const hasVariables = extractVariables(config.value).length > 0;
    const processedValue = hasVariables ? processTemplate(config.value, draft).processed : null;

    return (
        <div className="flex-1 min-w-0">
            <div className={`border rounded-lg overflow-hidden transition ${
                rowErrors.value
                    ? 'border-red-400 dark:border-red-500'
                    : 'border-gray-200 dark:border-gray-700'
            }`}>
                {/* Header - Config Key */}
                <div className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-3 py-1.5">
                    <p className="text-xs font-mono font-semibold text-gray-600 dark:text-gray-400">
                        {config.key}
                    </p>
                </div>

                {/* Value Input */}
                <textarea
                    placeholder="valor"
                    value={config.value}
                    rows={1}
                    onChange={(e) => {
                        onValueChange(config.id, e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onFocus={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    style={{ minHeight: '34px' }}
                    className={`w-full px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 transition resize-none overflow-hidden leading-snug ${
                        rowErrors.value
                            ? 'focus:ring-red-200 dark:focus:ring-red-800'
                            : 'focus:ring-blue-200 dark:focus:ring-blue-800'
                    }`}
                />

                {/* Footer - Preview with Expanded Values */}
                {hasVariables && processedValue !== config.value && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-1.5">
                        <div className="text-xs font-mono">
                            <HighlightedPreview originalValue={config.value} processedValue={processedValue} configs={draft} />
                        </div>
                    </div>
                )}
            </div>
            {rowErrors.value && (
                <p className="text-xs text-red-500 mt-0.5 pl-1">{rowErrors.value}</p>
            )}
        </div>
    );
};


const configsApi = {
    list: async () => {
        const token = localStorage.getItem('access_token');
        const response = await api.get('/company/configs', {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },
    save: async (configs) => {
        const token = localStorage.getItem('access_token');
        const response = await api.put('/company/configs', { configs }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },
};

const CompanyConfigsPage = () => {
    const [savedConfigs, setSavedConfigs] = useState([]);
    const [draft, setDraft] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [errors, setErrors] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await configsApi.list();
                const items = (data.items || []).map(c => ({ id: c.id, key: c.key, value: c.value }));
                setSavedConfigs(items);
                setDraft(items.map(c => ({ ...c })));
            } catch (err) {
                toast.error('Erro ao carregar configurações');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const markChanged = () => setHasChanges(true);

    const handleKeyChange = (id, value) => {
        setDraft(prev => prev.map(c => c.id === id ? { ...c, key: value } : c));
        setErrors(prev => ({ ...prev, [id]: { ...prev[id], key: undefined } }));
        markChanged();
    };

    const handleValueChange = (id, value) => {
        setDraft(prev => prev.map(c => c.id === id ? { ...c, value } : c));
        setErrors(prev => ({ ...prev, [id]: { ...prev[id], value: undefined } }));
        markChanged();
    };

    const validate = () => {
        const newErrors = {};
        let valid = true;
        draft.forEach(c => {
            const rowErrors = {};
            if (!c.value.trim()) { rowErrors.value = 'Obrigatório'; valid = false; }
            if (Object.keys(rowErrors).length) newErrors[c.id] = rowErrors;
        });
        setErrors(newErrors);
        return valid;
    };

    const handleCancel = () => {
        setDraft(savedConfigs.map(c => ({ ...c })));
        setErrors({});
        setSearchTerm('');
        setHasChanges(false);
    };

    const handleSave = async () => {
        if (!validate()) {
            toast.error('Corrija os erros antes de salvar');
            return;
        }
        setSaving(true);
        try {
            const payload = draft.map(c => ({ key: c.key.trim(), value: c.value }));
            const data = await configsApi.save(payload);
            const items = (data.items || []).map(c => ({ id: c.id, key: c.key, value: c.value }));
            setSavedConfigs(items);
            setDraft(items.map(c => ({ ...c })));
            setHasChanges(false);
            toast.success('Configurações salvas com sucesso');
        } catch (err) {
            toast.error('Erro ao salvar configurações');
        } finally {
            setSaving(false);
        }
    };

    const filteredDraft = draft.filter(c =>
        c.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const isEmpty = draft.length === 0;
    const noResults = !isEmpty && filteredDraft.length === 0 && searchTerm;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <span className="material-icons animate-spin text-blue-500 text-3xl">refresh</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Configurações</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Configure aqui suas variáveis globais do sistema
                    </p>
                </div>
                {hasChanges && (
                    <div className="flex items-center gap-2">
                        <Button onClick={handleCancel} disabled={saving} variant="secondary">
                            <span className="material-icons" style={{ fontSize: 16 }}>undo</span>
                            Cancelar
                        </Button>
                        <Button onClick={handleSave} disabled={saving} loading={saving} variant="primary">
                            <span className="material-icons" style={{ fontSize: 16 }}>{saving ? 'hourglass_empty' : 'save'}</span>
                            {saving ? 'Salvando...' : 'Salvar alterações'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
                    <span className="material-icons text-gray-400" style={{ fontSize: 18 }}>search</span>
                    <input
                        type="text"
                        placeholder="Filtrar por nome da chave..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                        >
                            <span className="material-icons" style={{ fontSize: 18 }}>close</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex flex-col" style={{ height: '480px' }}>

                {/* Scrollable rows */}
                <div className="flex-1 overflow-y-auto min-h-0">

                    {isEmpty && (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full mb-4 flex-shrink-0">
                                <span className="material-icons text-gray-400 dark:text-gray-500 text-3xl">tune</span>
                            </div>
                            <p className="font-medium text-gray-600 dark:text-gray-400 mb-1">Nenhuma configuração disponível</p>
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                                Não há configurações para editar no momento
                            </p>
                        </div>
                    )}

                    {noResults && (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <span className="material-icons text-gray-300 dark:text-gray-600 text-3xl mb-2">search_off</span>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Nenhuma chave encontrada para <strong>"{searchTerm}"</strong>
                            </p>
                        </div>
                    )}

                    {filteredDraft.map((config) => (
                        <div
                            key={config.id}
                            className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700/60 transition last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                        >
                            <ConfigValueInput
                                config={config}
                                draft={draft}
                                errors={errors}
                                onValueChange={handleValueChange}
                            />
                        </div>
                    ))}

                </div>

                {/* Footer */}
                {!isEmpty && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 rounded-b-lg">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {searchTerm && filteredDraft.length !== draft.length ? (
                                <>
                                    Mostrando <span className="font-medium text-gray-900 dark:text-gray-100">{filteredDraft.length}</span> de <span className="font-medium text-gray-900 dark:text-gray-100">{draft.length}</span> registro{draft.length !== 1 ? 's' : ''}
                                </>
                            ) : (
                                <>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">{draft.length}</span> registro{draft.length !== 1 ? 's' : ''} no total
                                </>
                            )}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyConfigsPage;
